import { useMemo, useCallback, useEffect } from "react";
import moment from "moment-timezone";
import { Card, CardHeader } from "reactstrap";
import MyTimesheetTable from "../../TimeSheet/components/TimeSheetTable";
import MyTimesheetUserTable from "../../TimeSheet/Home/Table";
import AddTimesheetNote from "../../TimeSheet/components/TimeSheetTable/AddTimesheetNote";
import Actions from "../../TimeSheet/Home/Actions";
import Summary from "../../TimeSheet/Home/Actions/Summary";
import History from "../../TimeSheet/Home/Actions/HistoryDrawer";
import { TableContext } from "../../../components/Table/context";
import { useMyTimeSheets } from "../../TimeSheet/api/useMyTimeSheets";
import { useRefetch } from "../../TimeSheet/Home/hooks/useRefetch";
import { useElementHeight } from "../../../utils/hooks/useElementHeight";
import { useCompany } from "../../../utils/hooks/company";
import { useOverview } from "../../TimeSheet/components/Topbar/context";
import { useColumns } from "../../TimeSheet/components/TimeSheetTable/useColumns";
import { useSummary } from "../../TimeSheet/Home/hooks/useSummary";
import { useRemoveActions } from "../../TimeSheet/Home/hooks/useRemoveActions";
import { useDateActions } from "../../TimeSheet/Home/hooks/useDateActions";
import { useAccess } from "../../../utils/hooks/access";
import { useEdit } from "../../TimeSheet/Home/hooks/useEdit";
import { useDateIdMap } from "../../TimeSheet/components/TimeSheetTable/hooks/useDateIdMap";
import { useBatchUpdate } from "../../TimeSheet/Home/hooks/useBatchUpdate";
import { useTimeSheetTable } from "../../TimeSheet/components/TimeSheetTable/hooks/useTable";
import { useVisible } from "../../../utils/hooks/useVisible";
import { useDateTimesheetMap } from "../../TimeSheet/components/TimeSheetTable/hooks/useDateTimesheetMap";
import { useFilters } from "../../TimeSheet/filtersContext";
import { useExpanded, useTable } from "react-table";
import { useColumns as useTeamColumns } from "../../TimeSheet/Team/Table/useColumns";

import { usePreferences } from "../../Settings/Configuration/hooks/usePreferences";
import { useColumnsPreferences } from "../../Settings/Configuration/hooks/useColumnsPreferences";
import { useColumnsVisibilityPreferences } from "../../Settings/Configuration/hooks/useColumnsVisibilityPreferences";
import { useMyTimeSheetUserOverall } from "../../TimeSheet/Home/Content";
import { usePayGroupItems } from "../../TimeSheet/components/TimeSheetTable/hooks/usePayGroupItems";
import { createPortal } from "react-dom";
import CopyTimesheetModal from "../../TimeSheet/Home/Actions/CopyTimesheetModal";
import { useWaiveTimeSheet } from "../../TimeSheet/api/useWaiveTimeSheet";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useMissPunchModal } from "../../TimeSheet/Home/hooks/useMissPunchModal";
import { useInOutDateModal } from "../../TimeSheet/Team/Table/TimeSheetTable/hooks/useInOutDateModal";
import { useSortTimeSheets } from "../../TimeSheet/components/TimeSheetTable/hooks/useSortTimeSheets";

function Timesheet({ refetchTimesheet }) {
	const { t } = useTranslation();
	const company = useCompany();
	const date = useMemo(
		() => moment().format(company?.settings?.dateFormat || "MM/DD/YYYY"),
		[company?.settings?.dateFormat],
	);
	const from = date;
	const to = date;

	const hideDailySummary = usePreferences("myTimesheet.hideDailySummary");
	const hideWeeklySummary = usePreferences("myTimesheet.hideWeeklySummary");
	const hideClockDatePicker = usePreferences("myTimesheet.timeSheet.hideClockDatePicker");

	const [filters] = useFilters();
	const { ref: actionsRef, height: actionsHeight } = useElementHeight();

	const { fetch: baseFetchOverview } = useOverview();
	const fetchOverview = useCallback(
		(config) => {
			baseFetchOverview({
				url: "/time-sheet/overview",
				...config,
			});
		},
		[baseFetchOverview],
	);

	const {
		scheduledHours: scheduleInfo,
		clockExceptions,
		data: { result: baseData },
		isLoading: loadingTimeSheet,
		setTotalItems,
		setData,
		mutate,
		mutateClockExceptions,
		mutateScheduledHours,
		missPunchRequest: _missPunchRequest
	} = useMyTimeSheets({ from, to, filters });
    const data = useSortTimeSheets(baseData);

	const {
		data: user,
		isLoading: loadingUser,
		mutate: mutateUser
	} = useMyTimeSheetUserOverall({ from, to, filters });
	const refetchUser = useCallback(() => {
		mutateUser();
	}, [mutateUser]);

	const refetchTimeSheets = useCallback(() => {
		mutate();
		mutateClockExceptions();
		mutateScheduledHours();
	}, [mutate, mutateScheduledHours, mutateClockExceptions]);

	const { refetchOverview, refetchOverviewAndTimeSheets } = useRefetch({
		refetchUser,
		fetchOverview,
		refetchTimeSheets,
		filters,
	});

	const { modal: removeModal, remove } = useRemoveActions({
		refetchOverview,
		setData,
		setTotalItems,
	});

	const { approve, unapprove, lock, unlock } = useDateActions(
		refetchOverviewAndTimeSheets,
	);

	const {
		visible: editVisible,
		open: openEdit,
		close: closeEdit,
		selected: editSelected,
		loading: editLoading,
		error: editError,
		submit: editSubmit,
	} = useEdit({ refetch: refetchOverviewAndTimeSheets });

	const {
		visible: summaryVisible,
		selected: summaryRange,
		openDaySummary,
		openAllSummary,
		close: closeSummary,
	} = useSummary();

	const {
		visible: historyVisible,
		selected: selectedTimesheet,
		open: openHistory,
		close: closeHistory,
	} = useVisible();

	const {
		visible: copyModalVisible,
		selected: selectedInfo,
		open: openCopyModal,
		close: closeCopyModal,
	} = useVisible();

	const { hasAccess: canViewRates } = useAccess("canViewMyRates");
	const { hasAccess: canCreate } = useAccess(
		"timeSheet.canCreateMyTimesheet",
	);
	const { hasAccess: canViewMyOriginalClocks } = useAccess(
		"timeSheet.canViewMyOriginalClocks",
	);
	const { hasAccess: canViewHistory } = useAccess(
		"timeSheet.canViewHistoryMyTimesheet",
	);
	const { hasAccess: canViewExceptions } = useAccess(
		"timeSheet.canViewClockExceptionsMyTimesheet",
	);
	const { hasAccess: canEdit } = useAccess("timeSheet.canCreateMyTimesheet" /**"timeSheet.canEditMyTimesheet" */);
	const { hasAccess: canChangeClockDate } = useAccess("timeSheet.canChangeClockDateMyTimesheet");

	const { waiveMyMealPremium: _waive } = useWaiveTimeSheet();
	const waive = useCallback((date) => {
		_waive(date, () => {
			refetchOverviewAndTimeSheets();
			toast.success(t("request-created-successfully"))
		});
	}, [refetchOverviewAndTimeSheets, _waive, t]);

	const missPunchRequest = useCallback((id, data) => {
		_missPunchRequest(id, data, () => {
			refetchOverviewAndTimeSheets();
			toast.success(t("request-created-successfully"))
		});
	}, [_missPunchRequest, refetchOverviewAndTimeSheets, t]);

	const { open: openMissPunchModal, modal: missPunchModal } = useMissPunchModal({ missPunchRequest });
	const { open: openInOutDateModal, modal: inOutDateModal } = useInOutDateModal();

	const dateIdMap = useDateIdMap({ data });
	const dateTimesheetMap = useDateTimesheetMap({ data });
	const columns = useColumns({
		user,
		scheduleInfo,
		canCreate,
		canEdit,
		dateIdMap,
		dateTimesheetMap,
		approve,
		unapprove,
		lock,
		unlock,
		viewSummary: !hideDailySummary && openDaySummary,
		edit: editSubmit ? openEdit : null,
		view: (canViewMyOriginalClocks || canViewHistory || canViewExceptions) ? openHistory : undefined,
		remove,
		copy: openCopyModal,
		isMyTimesheet: true,
		clockExceptions,
		waive,
		openMissPunchModal,
		openInOutDateModal: (canChangeClockDate && !hideClockDatePicker) ? openInOutDateModal : undefined,
		canViewRates,
	});

	const { submit: batchUpdateSubmit, loading: batchUpdateLoading } =
		useBatchUpdate({
			refetch: refetchOverviewAndTimeSheets,
			filters,
		});

	const payGroupItems = usePayGroupItems(user);

	const table = useTimeSheetTable({
		user,
		columns: useColumnsPreferences(columns, "myTimesheet"),
		data,
		dateIdMap,
		batchUpdate: batchUpdateSubmit,
		submitting: batchUpdateLoading,
		payGroupItems,
	});

	const hiddenColumnsMap = useColumnsVisibilityPreferences("myTimesheet");
	useEffect(() => {
		if (hiddenColumnsMap) {
			table.setHiddenColumns(Object.keys(hiddenColumnsMap));
		}
	}, [table, hiddenColumnsMap]);

	useEffect(() => {
		const controller = new AbortController();
		fetchOverview({
			params: { from, to, filters },
			signal: controller.signal,
		});
		return () => controller.abort();
	}, [fetchOverview, from, to, filters]);

	// WORKAROUND
	useEffect(() => {
		if (refetchTimesheet > 0) {
			refetchTimeSheets();
		}
	}, [refetchTimesheet, refetchTimeSheets]);

	const teamTable = useTable(
		{
			data: useMemo(() => {
				if (!user) {
					return [];
				}
				return [user];
			}, [user]),
			columns: useColumnsPreferences(useTeamColumns({ viewSummary: !hideDailySummary && openAllSummary, canViewRates }), "timeSheetTeam"),
			defaultColumn: {
				hideable: true,
				alwaysVisible: false,
			},
			getRowId: useCallback(({ id }) => id, []),
			autoResetExpanded: false,
		},
		useExpanded,
	);

	useEffect(() => {
		teamTable.toggleAllRowsExpanded(true);
	}, [user?.id, teamTable]);

	useEffect(() => {
		if (summaryVisible || historyVisible) {
			window.scrollTo(0, 0);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}

		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [summaryVisible, historyVisible]);

	return (
		<TableContext.Provider value={table}>
			<Card innerRef={actionsRef} className="mb-0 position-static">
				<CardHeader className="border-top p-3">
					<Actions
						columns={table.columns}
						selectedRowIds={table.state.selectedRowIds}
						viewSummary={!hideWeeklySummary && openAllSummary}
						user={user}
						filters={filters}
						approve={approve}
						unapprove={unapprove}
						lock={lock}
						unlock={unlock}
						remove={remove}
					/>
				</CardHeader>
			</Card>

			<div>
				<TableContext.Provider value={teamTable}>
					<MyTimesheetUserTable loading={loadingUser}>
						<TableContext.Provider value={table}>
							<MyTimesheetTable loading={loadingTimeSheet} />
						</TableContext.Provider>
					</MyTimesheetUserTable>
				</TableContext.Provider>


				{editSubmit && user && (
					<AddTimesheetNote
						visible={editVisible}
						close={closeEdit}
						onFinish={editSubmit}
						error={editError}
						submitting={editLoading}
						values={editSelected}
					/>
				)}
			</div>

			{summaryVisible && user && createPortal(
				<Summary
					open={summaryVisible}
					close={closeSummary}
					user={user}
					range={summaryRange}
					filters={filters}
					canViewRates={canViewRates}
				/>
				, document.body
			)}

			{historyVisible && createPortal(
				<History
					open={historyVisible}
					close={closeHistory}
					user={user}
					selected={selectedTimesheet}
					isMyTimesheet={true}
					canViewRates={canViewRates}
				/>
				, document.body
			)}

			{copyModalVisible && (
				<CopyTimesheetModal
					isOpen={copyModalVisible}
					close={closeCopyModal}
					timesheet={selectedInfo}
					user={user}
					dateTimesheetMap={dateTimesheetMap}
					refetch={refetchOverviewAndTimeSheets}
					filters={{ filters }}
					canViewRates={true}
				/>
			)}

			{removeModal}

			{missPunchModal}

			{inOutDateModal}
		</TableContext.Provider>
	);
}

export default Timesheet;
